// Module 12 — environment-aware quality selection. Collects cheap device + ambient signals (battery,
// Save-Data, connection type, CPU cores, memory, pointer/touch, screen size, software-WebGL flag) with
// SAFE FALLBACKS — every browser API here is optional/undefined somewhere, so each probe is wrapped to
// degrade to a neutral default instead of throwing. Pure functions over an injectable `Navigator`-like
// seam so the whole module is node-testable (no DOM required). It NEVER touches the command stream,
// selection, or snapshot sequence — only RECOMMENDS a starting tier; the governor floats from there on
// live frame timing. Determinism (R15) holds on every device.
//
// The selector is deliberately MULTI-LAYERED (user requirement: "consider many layers beyond resolution
// and Hz"): software-WebGL → Save-Data/connection → form-factor → battery state → CPU/memory class.
// Each layer can only LOWER the recommendation from the hardware baseline, never raise it — so a weak
// signal never pretends a device is stronger than it is. The governor still probes upward afterwards.
import type { QualityTier } from "../viz/index";

/** Effective network connection class. `unknown` when the API is absent (don't penalize the device). */
export type EffectiveType = "4g" | "3g" | "2g" | "slow-2g" | "unknown";

/** Battery state, or `null` when the Battery API is unavailable (Safari, Firefox). */
export interface BatteryState {
  /** 0..1 charge level. */
  readonly level: number;
  /** True when plugged into mains power. */
  readonly charging: boolean;
}

/**
 * All ambient + device signals the quality system considers. Every field degrades gracefully:
 * `null`/`unknown`/`false` when the browser doesn't expose it. NEVER throws to read.
 */
export interface EnvSignals {
  readonly battery: BatteryState | null;
  /** navigator.connection.saveData — user asked the browser to reduce data usage. */
  readonly saveData: boolean;
  readonly effectiveType: EffectiveType;
  /** navigator.deviceMemory in GB (Chromium-only; null elsewhere → NOT penalizing). */
  readonly memory: number | null;
  readonly cores: number;
  readonly coarsePointer: boolean;
  readonly touch: boolean;
  readonly smallScreen: boolean;
  /** SwiftShader / llvmpipe / software rasterizer detected → cannot hold the full scene. */
  readonly softwareWebGl: boolean;
  /**
   * navigator.userAgentData.mobile (Chromium User-Agent Client Hints) — the browser/OS's OWN
   * form-factor classification, `null` where the API is absent (Firefox, Safari). This is a
   * stronger phone-vs-tablet signal than screen size alone: an Android PHONE reports `true`
   * regardless of the user's OS display-scale setting (which can push a large phone's CSS-px
   * screen size above a fixed threshold), and an Android TABLET typically reports `false` even
   * when its physical screen happens to be small (the 7-8" class where screen-size thresholds
   * overlap with phones). See app/mobile.ts isMobilePhone(), which prefers this over smallScreen
   * whenever it's available.
   */
  readonly uaMobile: boolean | null;
}

/**
 * Injectable navigator/screen/document seams so the read is node-testable. In production these default
 * to the real globals; tests pass fakes. All accessors return `undefined`-tolerant values.
 */
export interface EnvDeps {
  readonly userAgent?: string | undefined;
  readonly hardwareConcurrency?: number | undefined;
  readonly deviceMemory?: number | undefined;
  readonly maxTouchPoints?: number | undefined;
  readonly connection?: { saveData?: boolean | undefined; effectiveType?: string | undefined } | undefined;
  readonly matchMedia?: ((q: string) => { matches: boolean }) | undefined;
  readonly screenWidth?: number | undefined;
  readonly screenHeight?: number | undefined;
  /** Factory returning a fresh WebGL context's UNMASKED_RENDERER string, or null if none. */
  readonly webglRenderer?: (() => string | null) | undefined;
  /** navigator.userAgentData?.mobile — undefined where the Client Hints API is absent. */
  readonly uaMobile?: boolean | undefined;
}

/** Detect a software / headless WebGL rasterizer (SwiftShader, llvmpipe, "software"). */
function detectSoftwareWebGl(renderer: () => string | null): boolean {
  try {
    const r = renderer();
    if (r === null) return true; // no WebGL at all — definitely start low
    return /swiftshader|software|llvmpipe/i.test(r);
  } catch {
    return false; // a thrown extension probe is not proof of software rendering
  }
}

/**
 * Read every ambient signal synchronously. Battery is resolved separately via {@link watchBattery}
 * (it's async on Chromium); here it stays `null` until that promise settles. Safe to call at boot.
 */
export function readEnvSignals(deps: EnvDeps): EnvSignals {
  const saveData = (() => {
    try {
      return Boolean(deps.connection?.saveData);
    } catch {
      return false;
    }
  })();
  const rawEt = (() => {
    try {
      return deps.connection?.effectiveType;
    } catch {
      return undefined;
    }
  })();
  const effectiveType: EffectiveType =
    rawEt === "4g" || rawEt === "3g" || rawEt === "2g" || rawEt === "slow-2g" ? rawEt : "unknown";

  const coarsePointer = (() => {
    try {
      return typeof deps.matchMedia === "function" ? deps.matchMedia("(pointer: coarse)").matches : false;
    } catch {
      return false;
    }
  })();
  const touch = (() => {
    try {
      return (deps.maxTouchPoints ?? 0) > 0;
    } catch {
      return false;
    }
  })();
  const smallScreen = (() => {
    try {
      const w = deps.screenWidth ?? Number.POSITIVE_INFINITY;
      const h = deps.screenHeight ?? Number.POSITIVE_INFINITY;
      // 480 was too tight (2026-07-06 pt.2, adversarial re-audit): modern large-screen phones report a
      // CSS-px short side ABOVE 480 (several Android flagship "phablets" sit at 480-500+ depending on OS
      // zoom), and this is the DECIDING signal on every browser without navigator.userAgentData — i.e.
      // Safari on every iPhone, and Firefox everywhere, since isMobilePhone()'s fallback ANDs
      // coarsePointer+touch+smallScreen. A real phone falling through this threshold gets the DESKTOP
      // corner-dock layout (tiny 22px buttons, no orientation gate, launcher chip bar undocked) instead
      // of the phone corner-tab layout — the concrete, reproducible cause of a real phone's control panel
      // reading as "not accessible." Raised to 560 (still comfortably under a 7-8in tablet's ~600+ short
      // side) so a genuinely large PHONE still reads as small; a real tablet keeps clearing it.
      return Math.min(w, h) <= 560;
    } catch {
      return false;
    }
  })();

  return {
    battery: null, // filled in async by watchBattery
    saveData,
    effectiveType,
    memory: (() => {
      const m = deps.deviceMemory;
      return typeof m === "number" && m > 0 ? m : null;
    })(),
    cores: (() => {
      const c = deps.hardwareConcurrency;
      return typeof c === "number" && c > 0 ? c : 4;
    })(),
    coarsePointer,
    touch,
    smallScreen,
    softwareWebGl: deps.webglRenderer ? detectSoftwareWebGl(deps.webglRenderer) : false,
    uaMobile: typeof deps.uaMobile === "boolean" ? deps.uaMobile : null,
  };
}

/**
 * Multi-layered START-tier recommendation. Layers apply top-down; each can only LOWER from the
 * hardware baseline. The governor then probes upward on live frame timing, so a conservative start
 * never traps a capable device — it only avoids a bad first impression.
 *
 * Layers (in priority order):
 *  1. software-WebGL / no-WebGL        → low
 *  2. Save-Data or slow network (3g/2g)→ low  (cuts tile fetches)
 *  3. phone/tablet form factor         → medium
 *  4. battery < 30% and discharging    → one tier below the hardware suggestion
 *  5. CPU/memory class                 → ultra / high / medium baseline
 */
export function recommendedStartTier(env: EnvSignals): QualityTier {
  // Layer 1 — software rendering cannot hold the scene at any resolution.
  if (env.softwareWebGl) return "low";

  // Layer 5 (baseline) — computed first so layers 2/3/4 can lower it.
  const strongCores = env.cores >= 8;
  const strongMem = env.memory === null || env.memory >= 8; // null = unknown → don't penalize
  const midCores = env.cores >= 6;
  const midMem = env.memory !== null && env.memory >= 4;
  let base: QualityTier;
  if (strongCores && strongMem) base = "ultra";
  else if (midCores || midMem) base = "high";
  else base = "medium";

  // Layer 2 — Save-Data or a metered/slow network: a HARD constraint — minimize tile fetches + effects
  // regardless of CPU. The user explicitly asked the browser to reduce data, or is on a metered link
  // where every tile fetch costs money/bytes; quality should not climb back up via probing here.
  const slowNet = env.saveData || env.effectiveType === "3g" || env.effectiveType === "2g" || env.effectiveType === "slow-2g";
  if (slowNet) base = "low";

  // Layer 3 — phone/tablet form factor: medium ceiling (governor may climb higher if it's a fast tablet).
  if ((env.coarsePointer && env.touch) || env.smallScreen) base = lower(base);

  // Layer 4 — low battery off-mains: one more step down to extend runtime.
  const lowBattery = env.battery !== null && !env.battery.charging && env.battery.level < 0.3;
  if (lowBattery) base = lower(base);

  return base;
}

const TIER_ORDER: readonly QualityTier[] = ["ultra", "highplus", "high", "mediumplus", "medium", "low"];

function lower(tier: QualityTier): QualityTier {
  const i = TIER_ORDER.indexOf(tier);
  return i < 0 ? "medium" : (TIER_ORDER[Math.min(i + 1, TIER_ORDER.length - 1)] ?? "low");
}

/**
 * Subscribe to the (Chromium-only, async) Battery API. Resolves with a snapshot, and fires `onChange`
 * whenever charging state or level crosses a meaningful threshold. No-op (resolves null) where the API
 * is absent — the governor already booted with `battery: null` and runs purely on frame timing. The
 * returned unsubscribe stops listening even if the browser never supported the API.
 */
export interface BatteryWatch {
  readonly unsubscribe: () => void;
}

export function watchBattery(
  getBattery: (() => Promise<unknown>) | undefined,
  onChange: (b: BatteryState) => void,
): BatteryWatch {
  let unsubed = false;
  let listeners: Array<() => void> = [];
  if (typeof getBattery !== "function") {
    return { unsubscribe: () => { unsubed = true; } };
  }
  void Promise.resolve()
    .then(() => getBattery())
    .then((raw) => {
      if (unsubed || !raw) return;
      // Structural read — BatteryManager shape across Chromium. Field access is non-throwing under the
      // guards; we attach listeners and remember them for teardown.
      const b = raw as {
        level?: number;
        charging?: boolean;
        addEventListener?: (t: string, cb: () => void) => void;
        removeEventListener?: (t: string, cb: () => void) => void;
      };
      const read = (): BatteryState => ({
        level: typeof b.level === "number" ? b.level : 1,
        charging: Boolean(b.charging),
      });
      const onLevel = (): void => onChange(read());
      const onCharging = (): void => onChange(read());
      const attach = (): void => {
        if (typeof b.addEventListener === "function") {
          b.addEventListener("levelchange", onLevel);
          b.addEventListener("chargingchange", onCharging);
          listeners = [() => b.removeEventListener?.("levelchange", onLevel), () => b.removeEventListener?.("chargingchange", onCharging)];
        }
      };
      attach();
      onChange(read()); // emit the initial state
    })
    .catch(() => {
      /* Battery API rejected — leave the governor on frame-timing only. */
    });
  return {
    unsubscribe(): void {
      unsubed = true;
      for (const off of listeners) {
        try { off(); } catch { /* already gone */ }
      }
      listeners = [];
    },
  };
}
